import React, { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Users2, Upload, FileSpreadsheet, CheckCircle2, Sparkles } from "lucide-react";

const profileLabels = {
  call_center: "Call Center Focused",
  front_desk_ambiance: "Front-Desk / Ambiance",
  cash_documentation: "Cash & Documentation",
};

const MasterList = () => {
  const { branches } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importKind, setImportKind] = useState("auto");
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    profile_types: ["front_desk_ambiance"],
    branch_id: "",
    joining_date: new Date().toISOString().slice(0, 10),
    contact: "",
    role: "receptionist",
    remarks: "",
    calling_required: false,
    daily_calling_target: 30,
  });

  const load = () => {
    api.get(`/receptionists?include_inactive=${showInactive}`).then((r) => setRows(r.data));
  };
  useEffect(load, [showInactive]);

  const create = async () => {
    if (!form.name || !form.email || !form.branch_id || !form.profile_types?.length)
      return toast.error("Name, email, branch and at least one profile type are required");
    try {
      await api.post("/receptionists", form);
      toast.success("Receptionist added");
      setOpen(false);
      setForm({
        name: "",
        email: "",
        profile_types: ["front_desk_ambiance"],
        branch_id: "",
        joining_date: new Date().toISOString().slice(0, 10),
        contact: "",
        role: "receptionist",
        remarks: "",
        calling_required: false,
        daily_calling_target: 30,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggleCalling = async (id, val) => {
    await api.put(`/receptionists/${id}`, { calling_required: val });
    toast.success(val ? "Calling target ON" : "Calling target OFF");
    load();
  };

  const setCallingTarget = async (id, val) => {
    await api.put(`/receptionists/${id}`, { daily_calling_target: Number(val) || 0 });
    load();
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate receptionist? Historical data is preserved.")) return;
    await api.delete(`/receptionists/${id}`);
    toast.success("Deactivated");
    load();
  };

  const changeStatus = async (id, status) => {
    await api.put(`/receptionists/${id}`, { status });
    load();
  };

  const populateSample = async () => {
    if (!window.confirm("Populate sample team? This adds 3 branches and 5 receptionists if the database is empty. Existing records are preserved.")) return;
    try {
      const { data } = await api.post("/seed");
      if (data.message === "Already seeded") {
        toast.info("Sample data already exists. Use Add Receptionist to add more.");
      } else {
        toast.success(`Seeded ${data.branches} branches and ${data.receptionists} receptionists`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    }
  };

  const doImport = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choose an .xlsx file first");
    setImporting(true);
    setImportResult(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", importKind);
    try {
      const { data } = await api.post("/import/excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success(`Imported: ${data.receptionists_created} recs · ${data.dues_created} dues · ${data.targets_upserted} targets`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Team Directory</div>
          <h1 className="font-heading text-4xl text-white tracking-tight mt-1">Reception Master List</h1>
          <p className="text-slate-400 mt-2 max-w-2xl">
            Every new receptionist joins here. Their <span className="text-[#C9A24B]">Employee ID is stable</span> — even if a name changes, every calculation joins on ID.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-3 text-sm">
            <Label className="text-slate-400 text-xs uppercase font-mono tracking-widest">Show Inactive</Label>
            <Switch
              data-testid="show-inactive-toggle"
              checked={showInactive}
              onCheckedChange={setShowInactive}
              className="data-[state=checked]:bg-[#C9A24B]"
            />
          </div>

          {/* Populate Sample Team */}
          <Button
            onClick={populateSample}
            data-testid="populate-sample-btn"
            className="bg-[#1E293B] hover:bg-[#334155] text-[#C9A24B] border border-[#C9A24B]/40 h-11 px-5"
          >
            <Sparkles size={16} className="mr-2" /> Populate Sample Team
          </Button>

          {/* Import Excel */}
          <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) setImportResult(null); }}>
            <DialogTrigger asChild>
              <Button
                data-testid="import-excel-btn"
                className="bg-[#1E293B] hover:bg-[#334155] text-white border border-[#334155] h-11 px-5"
              >
                <Upload size={16} className="mr-2" /> Import Excel
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1E293B] border-[#334155] text-white max-w-xl">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2">
                  <FileSpreadsheet size={20} className="text-[#C9A24B]" />
                  Import from Excel
                </DialogTitle>
                <DialogDescription className="text-slate-400 text-sm">
                  Upload the legacy v5 tracker or a fresh sheet. Auto-detect scans every tab for
                  <span className="text-[#C9A24B]"> receptionists, due patients and monthly targets</span>.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase font-mono tracking-widest text-slate-400 mb-2 block">
                    What to import
                  </Label>
                  <Select value={importKind} onValueChange={setImportKind}>
                    <SelectTrigger data-testid="import-kind" className="bg-[#0F172A] border-[#334155] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                      <SelectItem value="auto">Auto-detect (recommended)</SelectItem>
                      <SelectItem value="receptionists">Receptionists only</SelectItem>
                      <SelectItem value="dues">Due Patients only</SelectItem>
                      <SelectItem value="targets">Targets only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs uppercase font-mono tracking-widest text-slate-400 mb-2 block">
                    Excel file (.xlsx)
                  </Label>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".xlsx,.xlsm"
                    data-testid="import-file"
                    className="block w-full text-sm text-slate-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border file:border-[#334155] file:bg-[#0F172A] file:text-[#C9A24B] file:cursor-pointer file:font-mono hover:file:bg-[#334155]"
                  />
                </div>

                <div className="rounded-lg bg-[#0F172A] border border-[#334155] p-4 text-xs text-slate-400 space-y-1.5 leading-relaxed">
                  <div className="text-[#C9A24B] font-mono uppercase tracking-widest text-[10px]">Expected columns</div>
                  <div><span className="text-slate-200">Receptionists</span>: Name · Email · Profile Type · Branch · Contact</div>
                  <div><span className="text-slate-200">Due Patients</span>: Patient Name · Contact · Amount · Remarks · Status</div>
                  <div><span className="text-slate-200">Targets</span>: Employee ID · Metric · Monthly Target · Weight</div>
                  <div className="text-slate-500 italic pt-1">
                    Auto-detect also scans the legacy v5 PATIENT DUE CLEAR RECORD tables inside daily-performance sheets.
                  </div>
                </div>

                {importResult && (
                  <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/40 p-4" data-testid="import-result">
                    <div className="flex items-center gap-2 text-emerald-400 mb-2">
                      <CheckCircle2 size={16} />
                      <div className="font-heading">Import complete</div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <div className="text-2xl font-heading text-white">{importResult.receptionists_created}</div>
                        <div className="text-[10px] uppercase font-mono tracking-widest text-slate-400">Receptionists</div>
                      </div>
                      <div>
                        <div className="text-2xl font-heading text-white">{importResult.dues_created}</div>
                        <div className="text-[10px] uppercase font-mono tracking-widest text-slate-400">Dues</div>
                      </div>
                      <div>
                        <div className="text-2xl font-heading text-white">{importResult.targets_upserted}</div>
                        <div className="text-[10px] uppercase font-mono tracking-widest text-slate-400">Targets</div>
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono mt-3">
                      Scanned {importResult.sheets_scanned?.length || 0} sheet(s)
                    </div>
                  </div>
                )}

                <Button
                  onClick={doImport}
                  data-testid="import-submit-btn"
                  disabled={importing}
                  className="w-full bg-[#C9A24B] hover:bg-[#D4AF60] text-[#0F172A] font-medium h-11"
                >
                  {importing ? "Importing…" : "Import Now"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Receptionist */}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-receptionist-btn"
                className="bg-[#C9A24B] hover:bg-[#D4AF60] text-[#0F172A] font-medium h-11 px-5"
              >
                <Plus size={16} className="mr-2" /> Add Receptionist
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1E293B] border-[#334155] text-white max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">New Reception Team Member</DialogTitle>
                <DialogDescription className="text-slate-400 text-sm">
                  Stable Employee ID is auto-generated. Profile Type controls which fields appear on their Daily Log.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <F label="Full Name">
                  <Input
                    data-testid="rec-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </F>
                <F label="Google Email">
                  <Input
                    data-testid="rec-email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </F>
                <F label="Profile Types (multi-select)">
                  <div className="space-y-2 p-3 rounded-lg bg-[#0F172A] border border-[#334155]">
                    {[
                      { k: "front_desk_ambiance", label: "Front Desk & Ambience" },
                      { k: "call_center", label: "Focused on Call" },
                      { k: "cash_documentation", label: "Cash & Documentation" },
                    ].map((p) => {
                      const checked = (form.profile_types || []).includes(p.k);
                      return (
                        <label key={p.k} className="flex items-center gap-2 cursor-pointer" data-testid={`rec-profile-${p.k}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const cur = new Set(form.profile_types || []);
                              if (e.target.checked) cur.add(p.k); else cur.delete(p.k);
                              setForm({ ...form, profile_types: Array.from(cur) });
                            }}
                            className="w-4 h-4 accent-[#C9A24B]"
                          />
                          <span className="text-sm text-slate-200">{p.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </F>
                <F label="Branch">
                  <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                    <SelectTrigger data-testid="rec-branch" className="bg-[#0F172A] border-[#334155] text-white">
                      <SelectValue placeholder="Select branch…" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                      {branches.map((b) => (
                        <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Joining Date">
                  <Input
                    type="date"
                    data-testid="rec-joining"
                    value={form.joining_date}
                    onChange={(e) => setForm({ ...form, joining_date: e.target.value })}
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </F>
                <F label="Contact">
                  <Input
                    data-testid="rec-contact"
                    value={form.contact}
                    onChange={(e) => setForm({ ...form, contact: e.target.value })}
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </F>
                <F label="Role">
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="rec-role" className="bg-[#0F172A] border-[#334155] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                      <SelectItem value="receptionist">Receptionist</SelectItem>
                      <SelectItem value="manager">Reception Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
                <div className="md:col-span-2 p-3 rounded-lg bg-[#0F172A] border border-[#334155]">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-white">Calling Target Required?</div>
                      <div className="text-xs text-slate-500 mt-1">
                        Turn OFF for receptionists where calling is not applicable.
                      </div>
                    </div>
                    <Switch
                      data-testid="rec-calling-required"
                      checked={form.calling_required}
                      onCheckedChange={(v) => setForm({ ...form, calling_required: v })}
                      className="data-[state=checked]:bg-[#059669]"
                    />
                  </div>
                  {form.calling_required && (
                    <div className="mt-3">
                      <Label className="text-xs uppercase font-mono tracking-widest text-slate-400 mb-2 block">
                        Daily Calling Target
                      </Label>
                      <Input
                        data-testid="rec-calling-target"
                        type="number"
                        min="0"
                        value={form.daily_calling_target}
                        onChange={(e) => setForm({ ...form, daily_calling_target: Number(e.target.value) || 0 })}
                        className="bg-[#1E293B] border-[#334155] text-white"
                      />
                    </div>
                  )}
                </div>
              </div>
              <Button
                onClick={create}
                data-testid="rec-save-btn"
                className="bg-[#C9A24B] hover:bg-[#D4AF60] text-[#0F172A] font-medium"
              >
                Create
              </Button>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="bg-[#1E293B] border-[#334155] p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users2 size={18} className="text-[#C9A24B]" />
          <h3 className="font-heading text-xl text-white">Team Roster</h3>
        </div>
        <div className="rounded-lg border border-[#334155] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-[#334155] hover:bg-transparent">
                <TableHead className="text-slate-400 font-mono text-xs uppercase">Employee ID</TableHead>
                <TableHead className="text-slate-400 font-mono text-xs uppercase">Name</TableHead>
                <TableHead className="text-slate-400 font-mono text-xs uppercase">Email</TableHead>
                <TableHead className="text-slate-400 font-mono text-xs uppercase">Profile</TableHead>
                <TableHead className="text-slate-400 font-mono text-xs uppercase">Branch</TableHead>
                <TableHead className="text-slate-400 font-mono text-xs uppercase">Calling</TableHead>
                <TableHead className="text-slate-400 font-mono text-xs uppercase">Target/day</TableHead>
                <TableHead className="text-slate-400 font-mono text-xs uppercase">Status</TableHead>
                <TableHead className="text-slate-400 font-mono text-xs uppercase text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const branch = branches.find((b) => b.branch_id === r.branch_id);
                return (
                  <TableRow key={r.employee_id} className="border-[#334155] hover:bg-slate-800/40">
                    <TableCell className="text-[#C9A24B] font-mono">{r.employee_id}</TableCell>
                    <TableCell className="text-white">{r.name}</TableCell>
                    <TableCell className="text-slate-400 text-sm">{r.email}</TableCell>
                    <TableCell className="text-slate-300 text-sm">
                      <div className="flex flex-wrap gap-1" data-testid={`rec-profiles-${r.employee_id}`}>
                        {(r.profile_types || [r.profile_type]).filter(Boolean).map((p) => (
                          <Badge key={p} className="bg-[#C9A24B]/10 text-[#C9A24B] border border-[#C9A24B]/40 text-[10px] font-mono">
                            {profileLabels[p] || p}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-300 text-sm">{branch?.name || r.branch_id}</TableCell>
                    <TableCell>
                      <Switch
                        data-testid={`calling-toggle-${r.employee_id}`}
                        checked={!!r.calling_required}
                        onCheckedChange={(v) => toggleCalling(r.employee_id, v)}
                        className="data-[state=checked]:bg-[#059669]"
                      />
                    </TableCell>
                    <TableCell>
                      {r.calling_required ? (
                        <Input
                          data-testid={`calling-target-${r.employee_id}`}
                          type="number"
                          min="0"
                          defaultValue={r.daily_calling_target || 0}
                          onBlur={(e) => {
                            if (Number(e.target.value) !== (r.daily_calling_target || 0)) {
                              setCallingTarget(r.employee_id, e.target.value);
                            }
                          }}
                          className="w-20 h-8 text-xs bg-[#0F172A] border-[#334155] text-white"
                        />
                      ) : (
                        <span className="text-slate-500 text-xs italic">N/A</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Select value={r.status} onValueChange={(v) => changeStatus(r.employee_id, v)}>
                        <SelectTrigger
                          data-testid={`rec-status-${r.employee_id}`}
                          className="w-32 h-8 text-xs bg-[#0F172A] border-[#334155] text-white"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="on_leave">On Leave</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={() => deactivate(r.employee_id)}
                        data-testid={`deactivate-${r.employee_id}`}
                        className="h-8 text-xs bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/40"
                      >
                        Deactivate
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-slate-500 py-8">
                    No receptionists.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

const F = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase font-mono tracking-widest text-slate-400 mb-2 block">{label}</Label>
    {children}
  </div>
);

export default MasterList;
